// The "as of" date picker shared by Reports › ∑: Variants and Sales History
// (FIG-429 · UC-252). Both tabs run the same trailing 12 months, so they share this.
//
// Contract (src/shared/ptDates.ts): "as of X" is all of X in Pacific time and the window is
// [(X + 1) - 12 months 00:00 PT, (X + 1) 00:00 PT). The picker opens on yesterday in PT, today
// can't be picked (the latest date is yesterday in PT), and that holds in any browser time zone
// because every date here is a PT calendar date computed from the shared helper, never from the
// browser's own clock.
import React, { useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatYmd, localDateToYmd, ptYesterday, trailing12Window, ymdToLocalDate } from '@/shared/ptDates';

interface AsOfDatePickerProps {
  /** The applied as-of date ('YYYY-MM-DD'); empty is the default and follows yesterday in PT. */
  asOfDate: string;
  /** Text on the trigger button for the effective as-of date. */
  buttonLabel: (asOfDate: string) => string;
  /** The new as-of date; '' when it is the default (yesterday PT), so the default keeps following yesterday. */
  onChange: (asOfDate: string) => void;
  /** Injectable clock for tests. */
  now?: Date;
}

export const AsOfDatePicker: React.FC<AsOfDatePickerProps> = ({ asOfDate, buttonLabel, onChange, now }) => {
  const yesterday = ptYesterday(now);
  const applied = asOfDate || yesterday;
  // The calendar starts on the applied date (re-synced each time the popover opens).
  const [pending, setPending] = useState<Date | undefined>(() => ymdToLocalDate(applied));
  const pendingYmd = pending ? localDateToYmd(pending) : undefined;
  const range = pendingYmd ? trailing12Window(pendingYmd) : undefined;

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) setPending(ymdToLocalDate(applied));
      }}
    >
      <PopoverTrigger asChild>
        <Button variant={applied === yesterday ? 'outline' : 'default'} className="text-sm">
          <CalendarIcon className="h-4 w-4 mr-2" />
          {buttonLabel(applied)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4 bg-popover" align="start">
        <div className="space-y-3">
          <div className="text-sm font-semibold">Trailing 12 months ending on:</div>
          <Calendar
            mode="single"
            selected={pending}
            onSelect={setPending}
            defaultMonth={pending}
            disabled={(date) => localDateToYmd(date) > yesterday}
            initialFocus
            className="p-3 pointer-events-auto rounded-md border"
          />
          {range && (
            <div className="text-xs text-muted-foreground text-center">
              {formatYmd(range.startDate)} → {formatYmd(range.asOf)}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 pt-1">
            {applied !== yesterday || (pendingYmd && pendingYmd !== yesterday) ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  setPending(ymdToLocalDate(yesterday));
                  onChange('');
                }}
              >
                Reset to yesterday
              </Button>
            ) : <div />}
            <Button
              size="sm"
              className="h-8"
              disabled={!pendingYmd}
              onClick={() => {
                if (pendingYmd) onChange(pendingYmd === yesterday ? '' : pendingYmd);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
